import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';

// ---- Types ----

export type TimeRange = '7d' | '30d' | '90d';

export interface EngagementOverview {
  dauMauRatio: number;
  dauMauTrend: number;
  avgSessionDuration: number;
  avgSessionDurationByType: { startup: number; investor: number };
  sessionDurationTrend: number;
  avgEventsPerSession: number;
  eventsPerSessionTrend: number;
  timeToFirstValue: number;
  timeToFirstValueByType: { startup: number; investor: number };
}

export interface FeatureAdoptionRow {
  feature: string;
  userType: 'startup' | 'investor';
  uniqueUsers7d: number;
  uniqueUsers30d: number;
  totalUses7d: number;
  adoptionRate: number;
  trend: number;
}

export interface JourneyStep {
  label: string;
  count: number;
  percentage: number;
  isHappyPath: boolean;
}

export interface JourneyData {
  steps: JourneyStep[];
  totalStart: number;
}

export interface SessionBucket {
  label: string;
  startups: number;
  investors: number;
}

export interface SessionDepthData {
  buckets: SessionBucket[];
  insightText: string;
  sessionsPerWeek: { week: string; startups: number; investors: number }[];
}

export interface SearchAnalytics {
  topFilters: { filter: string; uses: number }[];
  searchToViewRate: number;
  zeroResultsRate: number;
  topCategories: { category: string; views: number }[];
}

export interface ContentEngagement {
  completenessDistribution: { bucket: string; count: number }[];
  topSections: { section: string; views: number }[];
  documentUploadRate: number;
  completenessVsViews: { completeness: number; avgViews: number };
}

export interface MatchingQuality {
  matchesPerInvestorPerWeek: number;
  responseRate: number;
  avgResponseTimeHours: number;
  connectionQualityScore: number;
  unmatchedStartups: number;
  unmatchedInvestors: number;
}

export interface ChurnDataPoint {
  day: number;
  startupRetention: number;
  investorRetention: number;
}

export interface ChurnAnalysis {
  churnCurve: ChurnDataPoint[];
  lastActionBeforeChurn: { action: string; percentage: number }[];
  reactivationRate: number;
  reactivationRateByType: { startup: number; investor: number };
}

export interface ProductDashboardData {
  engagement: EngagementOverview;
  featureAdoption: FeatureAdoptionRow[];
  startupJourney: JourneyData;
  investorJourney: JourneyData;
  sessionDepth: SessionDepthData;
  searchAnalytics: SearchAnalytics;
  contentEngagement: ContentEngagement;
  matchingQuality: MatchingQuality;
  churnAnalysis: ChurnAnalysis;
}

// ---- Mock Data ----

function buildMockData(timeRange: TimeRange): ProductDashboardData {
  const multiplier = timeRange === '7d' ? 0.7 : timeRange === '30d' ? 1.0 : 1.3;

  return {
    engagement: {
      dauMauRatio: 22.4,
      dauMauTrend: 3.2,
      avgSessionDuration: 9.6,
      avgSessionDurationByType: { startup: 11.2, investor: 7.8 },
      sessionDurationTrend: 1.8,
      avgEventsPerSession: 13.4,
      eventsPerSessionTrend: -0.6,
      timeToFirstValue: 2.3,
      timeToFirstValueByType: { startup: 1.8, investor: 3.1 },
    },
    featureAdoption: [
      { feature: 'Inverio Score Calculator', userType: 'startup', uniqueUsers7d: 68, uniqueUsers30d: 142, totalUses7d: Math.round(312 * multiplier), adoptionRate: 79, trend: 5.2 },
      { feature: 'Data Room', userType: 'startup', uniqueUsers7d: 34, uniqueUsers30d: 67, totalUses7d: Math.round(186 * multiplier), adoptionRate: 37, trend: 12.1 },
      { feature: 'Company Profile', userType: 'startup', uniqueUsers7d: 82, uniqueUsers30d: 112, totalUses7d: Math.round(210 * multiplier), adoptionRate: 62, trend: -1.4 },
      { feature: 'Recommendations', userType: 'startup', uniqueUsers7d: 45, uniqueUsers30d: 98, totalUses7d: Math.round(156 * multiplier), adoptionRate: 54, trend: 8.3 },
      { feature: 'Messaging', userType: 'startup', uniqueUsers7d: 38, uniqueUsers30d: 64, totalUses7d: Math.round(224 * multiplier), adoptionRate: 36, trend: 2.7 },
      { feature: 'Document Upload', userType: 'startup', uniqueUsers7d: 22, uniqueUsers30d: 48, totalUses7d: Math.round(94 * multiplier), adoptionRate: 27, trend: 15.6 },
      { feature: 'Dealflow Browse', userType: 'investor', uniqueUsers7d: 38, uniqueUsers30d: 52, totalUses7d: Math.round(284 * multiplier), adoptionRate: 73, trend: 4.1 },
      { feature: 'DD Assistant', userType: 'investor', uniqueUsers7d: 28, uniqueUsers30d: 42, totalUses7d: Math.round(168 * multiplier), adoptionRate: 54, trend: 18.2 },
      { feature: 'Shortlist', userType: 'investor', uniqueUsers7d: 32, uniqueUsers30d: 46, totalUses7d: Math.round(142 * multiplier), adoptionRate: 63, trend: 6.8 },
      { feature: 'Portfolio View', userType: 'investor', uniqueUsers7d: 24, uniqueUsers30d: 34, totalUses7d: Math.round(96 * multiplier), adoptionRate: 46, trend: -3.2 },
      { feature: 'Contact Request', userType: 'investor', uniqueUsers7d: 16, uniqueUsers30d: 28, totalUses7d: Math.round(52 * multiplier), adoptionRate: 38, trend: 9.4 },
      { feature: 'Messaging', userType: 'investor', uniqueUsers7d: 30, uniqueUsers30d: 44, totalUses7d: Math.round(178 * multiplier), adoptionRate: 60, trend: 1.2 },
    ],
    startupJourney: {
      totalStart: 180,
      steps: [
        { label: 'Sign Up', count: 180, percentage: 100, isHappyPath: true },
        { label: 'Complete Profile', count: 142, percentage: 78.9, isHappyPath: true },
        { label: 'Calculate Score', count: 98, percentage: 54.4, isHappyPath: true },
        { label: 'Create Data Room', count: 67, percentage: 37.2, isHappyPath: true },
        { label: 'Get First View', count: 48, percentage: 26.7, isHappyPath: true },
        { label: 'Receive Contact', count: 34, percentage: 18.9, isHappyPath: true },
      ],
    },
    investorJourney: {
      totalStart: 104,
      steps: [
        { label: 'Sign Up', count: 104, percentage: 100, isHappyPath: true },
        { label: 'Set Criteria', count: 89, percentage: 85.6, isHappyPath: true },
        { label: 'Browse Dealflow', count: 71, percentage: 68.3, isHappyPath: true },
        { label: 'Shortlist Startup', count: 46, percentage: 44.2, isHappyPath: true },
        { label: 'Send Contact', count: 28, percentage: 26.9, isHappyPath: true },
        { label: 'Active Deal', count: 12, percentage: 11.5, isHappyPath: true },
      ],
    },
    sessionDepth: {
      buckets: [
        { label: '<1m', startups: 12, investors: 24 },
        { label: '1-2m', startups: 18, investors: 31 },
        { label: '2-5m', startups: 34, investors: 28 },
        { label: '5-10m', startups: 42, investors: 16 },
        { label: '10-20m', startups: 28, investors: 8 },
        { label: '20m+', startups: 14, investors: 4 },
      ],
      insightText: '50% of investor sessions are under 2 minutes — possible discovery problem',
      sessionsPerWeek: [
        { week: 'W1', startups: 320, investors: 180 },
        { week: 'W2', startups: 345, investors: 192 },
        { week: 'W3', startups: 362, investors: 204 },
        { week: 'W4', startups: 388, investors: 218 },
      ],
    },
    searchAnalytics: {
      topFilters: [
        { filter: 'Industry', uses: 842 },
        { filter: 'Funding Stage', uses: 716 },
        { filter: 'Location', uses: 534 },
        { filter: 'Revenue Range', uses: 428 },
        { filter: 'Team Size', uses: 312 },
        { filter: 'Founded Year', uses: 186 },
        { filter: 'Funding Amount', uses: 164 },
        { filter: 'Business Model', uses: 142 },
        { filter: 'Market Size', uses: 98 },
        { filter: 'Growth Rate', uses: 76 },
      ],
      searchToViewRate: 34.2,
      zeroResultsRate: 8.7,
      topCategories: [
        { category: 'SaaS / B2B', views: 486 },
        { category: 'FinTech', views: 342 },
        { category: 'HealthTech', views: 278 },
        { category: 'AI / ML', views: 264 },
        { category: 'CleanTech', views: 186 },
        { category: 'E-Commerce', views: 152 },
      ],
    },
    contentEngagement: {
      completenessDistribution: [
        { bucket: '0-20%', count: 18 },
        { bucket: '21-40%', count: 24 },
        { bucket: '41-60%', count: 38 },
        { bucket: '61-80%', count: 52 },
        { bucket: '81-100%', count: 48 },
      ],
      topSections: [
        { section: 'Company Overview', views: 1240 },
        { section: 'Financials', views: 986 },
        { section: 'Team', views: 874 },
        { section: 'Product', views: 762 },
        { section: 'Market', views: 648 },
        { section: 'Traction', views: 524 },
      ],
      documentUploadRate: 42.3,
      completenessVsViews: { completeness: 78, avgViews: 12.4 },
    },
    matchingQuality: {
      matchesPerInvestorPerWeek: 4.2,
      responseRate: 38.6,
      avgResponseTimeHours: 18.4,
      connectionQualityScore: 72,
      unmatchedStartups: 23,
      unmatchedInvestors: 8,
    },
    churnAnalysis: {
      churnCurve: [
        { day: 1, startupRetention: 82, investorRetention: 74 },
        { day: 3, startupRetention: 68, investorRetention: 58 },
        { day: 7, startupRetention: 56, investorRetention: 46 },
        { day: 14, startupRetention: 44, investorRetention: 36 },
        { day: 30, startupRetention: 34, investorRetention: 28 },
        { day: 60, startupRetention: 28, investorRetention: 22 },
        { day: 90, startupRetention: 24, investorRetention: 18 },
      ],
      lastActionBeforeChurn: [
        { action: 'Viewed Dashboard', percentage: 32 },
        { action: 'Browsed Dealflow', percentage: 22 },
        { action: 'Viewed Profile', percentage: 18 },
        { action: 'Sent Message', percentage: 12 },
        { action: 'Uploaded Document', percentage: 9 },
        { action: 'Other', percentage: 7 },
      ],
      reactivationRate: 14.2,
      reactivationRateByType: { startup: 16.8, investor: 11.4 },
    },
  };
}

// ---- Helpers ----

/** Convert a TimeRange string to a Postgres interval expression */
function toInterval(timeRange: TimeRange): string {
  if (timeRange === '7d') return '7 days';
  if (timeRange === '90d') return '90 days';
  return '30 days';
}

// ---- Hook ----

export function useProductDashboard(timeRange: TimeRange = '30d') {
  return useQuery<ProductDashboardData>({
    queryKey: ['product-dashboard', timeRange],
    queryFn: async () => {
      if (mode.showSampleData) {
        return buildMockData(timeRange);
      }

      const interval = toInterval(timeRange);

      // Pull all available views and direct-table queries in one parallel batch
      const [
        kpisRes,
        sessionRes,
        adoptionRes,
        matchingFunnelRes,
        retentionCohortsRes,
        startupFunnelRes,
        investorFunnelRes,
        sessionBucketsRes,
        sessionsPerWeekRes,
        searchEventsRes,
        contentSectionsRes,
        conversationsRes,
        churnLastActionRes,
      ] = await Promise.all([
        // Already-wired views
        supabase.from('v_executive_kpis').select('*').single(),
        supabase.from('v_session_analytics').select('*'),
        supabase.from('v_feature_adoption').select('*'),
        supabase.from('v_matching_funnel').select('*'),

        // NEW: retention cohorts view
        supabase.from('v_retention_cohorts').select('*').order('cohort_week', { ascending: true }),

        // NEW: journey funnel views
        supabase.from('v_startup_funnel').select('*').order('stage_order', { ascending: true }),
        supabase.from('v_investor_funnel').select('*').order('stage_order', { ascending: true }),

        // NEW: session depth — duration buckets from user_sessions
        supabase
          .from('user_sessions')
          .select('duration_seconds, platform')
          .gte('started_at', `now() - interval '${interval}'`)
          .not('duration_seconds', 'is', null),

        // NEW: sessions per week for trend (last 4 weeks, group by week & platform)
        supabase
          .from('user_sessions')
          .select('started_at, platform')
          .gte('started_at', `now() - interval '28 days'`)
          .not('duration_seconds', 'is', null),

        // NEW: search analytics — top filters and categories (single query, split client-side)
        supabase
          .from('analytics_events')
          .select('properties')
          .or(`event_name.like.search%,event_category.eq.search`)
          .gte('created_at', `now() - interval '${interval}'`)
          .not('properties', 'is', null),

        // NEW: content engagement — top sections (view_section, profile_view events)
        supabase
          .from('analytics_events')
          .select('event_name, properties')
          .or(`event_category.eq.content,event_name.like.%view_section%,event_name.like.%profile_view%`)
          .gte('created_at', `now() - interval '${interval}'`),

        // NEW: matching quality — response rate from conversations.
        //
        // This read the two unread counters and called a conversation
        // "answered" when either was > 0. That was always a proxy for the
        // wrong thing — an unread count means somebody has NOT read, not that
        // anybody replied — and since 20260918170000 the counters are
        // server-managed and no longer reset by any client, so they only ever
        // grow and the proxy would drift to 100%. Ask the real question
        // instead: did both sides actually speak?
        supabase
          .from('conversations')
          .select('id, messages(sender_type)')
          .gte('created_at', `now() - interval '${interval}'`),

        // NEW: churn — last action before churn (users silent 14+ days)
        supabase
          .from('analytics_events')
          .select('user_id, event_name, created_at')
          .lt('created_at', `now() - interval '14 days'`)
          .order('created_at', { ascending: false }),
      ]);

      const mock = buildMockData(timeRange);

      // ── Engagement: KPIs ──────────────────────────────────────────────────
      const kpis = kpisRes.data;
      if (kpis) {
        const dau = kpis.dau ?? 0;
        const mau = kpis.mau ?? 1;
        mock.engagement.dauMauRatio = mau > 0 ? Math.round((dau / mau) * 1000) / 10 : 0;
      }

      // ── Engagement: Session metrics ───────────────────────────────────────
      const sessions: Array<{
        platform: string | null;
        avg_duration_seconds: number | null;
        avg_events_per_session: number | null;
      }> = sessionRes.data ?? [];
      if (sessions.length > 0) {
        const avgDuration =
          sessions.reduce((sum, s) => sum + (s.avg_duration_seconds ?? 0), 0) / sessions.length;
        const avgEvents =
          sessions.reduce((sum, s) => sum + (s.avg_events_per_session ?? 0), 0) / sessions.length;
        mock.engagement.avgSessionDuration = Math.round((avgDuration / 60) * 10) / 10;
        mock.engagement.avgEventsPerSession = Math.round(avgEvents * 10) / 10;
      }

      // ── Feature Adoption ──────────────────────────────────────────────────
      const adoptionRows: Array<{
        feature: string;
        unique_users: number;
        total_events: number;
        adoption_pct: number;
      }> = (adoptionRes.data as typeof adoptionRows) ?? [];
      if (adoptionRows.length > 0) {
        for (const row of mock.featureAdoption) {
          const match = adoptionRows.find((a) =>
            a.feature?.toLowerCase().includes(row.feature.toLowerCase().split(' ')[0])
          );
          if (match) {
            row.uniqueUsers30d = match.unique_users;
            row.adoptionRate = Math.round(match.adoption_pct);
          }
        }
      }

      // ── Journeys: startup funnel ──────────────────────────────────────────
      const startupFunnelRows: Array<{ stage: string; stage_order: number; count: number }> =
        (startupFunnelRes.data as typeof startupFunnelRows) ?? [];
      if (startupFunnelRows.length > 0) {
        const totalStart = startupFunnelRows[0]?.count ?? 0;
        const stageLabels: Record<string, string> = {
          signed_up: 'Sign Up',
          onboarded: 'Complete Profile',
          profile_created: 'Calculate Score',
          scored: 'Create Data Room',
          data_room_connected: 'Get First View',
          actively_raising: 'Receive Contact',
        };
        mock.startupJourney = {
          totalStart,
          steps: startupFunnelRows.map((r) => ({
            label: stageLabels[r.stage] ?? r.stage,
            count: r.count,
            percentage: totalStart > 0 ? Math.round((r.count / totalStart) * 1000) / 10 : 0,
            isHappyPath: true,
          })),
        };
      }

      // ── Journeys: investor funnel ─────────────────────────────────────────
      const investorFunnelRows: Array<{ stage: string; stage_order: number; count: number }> =
        (investorFunnelRes.data as typeof investorFunnelRows) ?? [];
      if (investorFunnelRows.length > 0) {
        const totalStart = investorFunnelRows[0]?.count ?? 0;
        const stageLabels: Record<string, string> = {
          signed_up: 'Sign Up',
          onboarded: 'Set Criteria',
          profile_created: 'Browse Dealflow',
          has_dealflow: 'Shortlist Startup',
          has_messaged: 'Send Contact',
          invested: 'Active Deal',
        };
        mock.investorJourney = {
          totalStart,
          steps: investorFunnelRows.map((r) => ({
            label: stageLabels[r.stage] ?? r.stage,
            count: r.count,
            percentage: totalStart > 0 ? Math.round((r.count / totalStart) * 1000) / 10 : 0,
            isHappyPath: true,
          })),
        };
      }

      // ── Session Depth: duration buckets ───────────────────────────────────
      const rawSessions: Array<{ duration_seconds: number | null; platform: string }> =
        (sessionBucketsRes.data as typeof rawSessions) ?? [];
      if (rawSessions.length > 0) {
        const bucketDef = [
          { label: '<1m', min: 0, max: 60 },
          { label: '1-5m', min: 60, max: 300 },
          { label: '5-15m', min: 300, max: 900 },
          { label: '15-30m', min: 900, max: 1800 },
          { label: '30m+', min: 1800, max: Infinity },
        ];
        const buckets = bucketDef.map(({ label, min, max }) => {
          const inBucket = rawSessions.filter(
            (s) => s.duration_seconds !== null && s.duration_seconds >= min && s.duration_seconds < max
          );
          const startupPlatforms = new Set(['ios', 'android', 'web']);
          const startups = inBucket.filter((s) => startupPlatforms.has(s.platform)).length;
          const investors = inBucket.length - startups;
          return { label, startups, investors };
        });
        const totalInvestorSessions = buckets.reduce((sum, b) => sum + b.investors, 0);
        const shortInvestorSessions = buckets
          .filter((b) => b.label === '<1m' || b.label === '1-5m')
          .reduce((sum, b) => sum + b.investors, 0);
        const shortPct =
          totalInvestorSessions > 0
            ? Math.round((shortInvestorSessions / totalInvestorSessions) * 100)
            : 0;
        mock.sessionDepth = {
          ...mock.sessionDepth,
          buckets,
          insightText:
            shortPct > 40
              ? `${shortPct}% of investor sessions are under 5 minutes — possible discovery problem`
              : `Session depth looks healthy — ${100 - shortPct}% of investor sessions exceed 5 minutes`,
        };
      }

      // ── Session Depth: sessions per week trend ────────────────────────────
      const rawWeeklySessions: Array<{ started_at: string; platform: string }> =
        (sessionsPerWeekRes.data as typeof rawWeeklySessions) ?? [];
      if (rawWeeklySessions.length > 0) {
        // Group into 4 ISO weeks
        const weekMap: Map<string, { startups: number; investors: number }> = new Map();
        for (const s of rawWeeklySessions) {
          const d = new Date(s.started_at);
          // Get Monday of the week (ISO week)
          const day = d.getDay();
          const diff = d.getDate() - day + (day === 0 ? -6 : 1);
          d.setDate(diff);
          const weekKey = `${d.getFullYear()}-W${String(Math.ceil(d.getDate() / 7)).padStart(2, '0')}`;
          if (!weekMap.has(weekKey)) weekMap.set(weekKey, { startups: 0, investors: 0 });
          const entry = weekMap.get(weekKey)!;
          // We don't have role info in user_sessions — use platform as proxy
          // (all platforms grouped as startups; investors tracked by platform 'investor' if tagged)
          entry.startups += 1;
        }
        const sortedWeeks = Array.from(weekMap.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .slice(-4);
        if (sortedWeeks.length >= 2) {
          mock.sessionDepth.sessionsPerWeek = sortedWeeks.map(([week, counts], i) => ({
            week: `W${i + 1}`,
            startups: counts.startups,
            investors: counts.investors,
          }));
        }
      }

      // ── Search Analytics: top filters and categories ─────────────────────
      const searchEventRows: Array<{ properties: Record<string, unknown> | null }> =
        (searchEventsRes.data as typeof searchEventRows) ?? [];
      if (searchEventRows.length > 0) {
        // Tally filter usages
        const filterCounts: Map<string, number> = new Map();
        const categoryCounts: Map<string, number> = new Map();
        for (const row of searchEventRows) {
          const props = row.properties ?? {};
          const filter = props['filter'] as string | undefined;
          const category = props['category'] as string | undefined;
          if (filter) filterCounts.set(filter, (filterCounts.get(filter) ?? 0) + 1);
          if (category) categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
        }
        const topFilters = Array.from(filterCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([filter, uses]) => ({ filter, uses }));
        const topCategories = Array.from(categoryCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([category, views]) => ({ category, views }));

        if (topFilters.length > 0) mock.searchAnalytics.topFilters = topFilters;
        if (topCategories.length > 0) mock.searchAnalytics.topCategories = topCategories;

        // Search-to-view rate: events where properties has result_count > 0 / total search events
        const withResults = searchEventRows.filter((r) => {
          const rc = (r.properties ?? {})['result_count'];
          return rc !== undefined && Number(rc) > 0;
        }).length;
        if (searchEventRows.length > 0) {
          mock.searchAnalytics.searchToViewRate =
            Math.round((withResults / searchEventRows.length) * 1000) / 10;
        }
        const zeroResults = searchEventRows.filter((r) => {
          const rc = (r.properties ?? {})['result_count'];
          return rc !== undefined && Number(rc) === 0;
        }).length;
        if (searchEventRows.length > 0) {
          mock.searchAnalytics.zeroResultsRate =
            Math.round((zeroResults / searchEventRows.length) * 1000) / 10;
        }
      }

      // ── Content Engagement: top sections ──────────────────────────────────
      const contentRows: Array<{ event_name: string; properties: Record<string, unknown> | null }> =
        (contentSectionsRes.data as typeof contentRows) ?? [];
      if (contentRows.length > 0) {
        const sectionCounts: Map<string, number> = new Map();
        for (const row of contentRows) {
          const props = row.properties ?? {};
          // Try property 'section', 'section_name', or fall back to event_name
          const section =
            (props['section'] as string | undefined) ??
            (props['section_name'] as string | undefined) ??
            row.event_name;
          sectionCounts.set(section, (sectionCounts.get(section) ?? 0) + 1);
        }
        const topSections = Array.from(sectionCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([section, views]) => ({ section, views }));
        if (topSections.length > 0) {
          mock.contentEngagement.topSections = topSections;
        }
      }

      // ── Matching Quality: response rate from conversations ────────────────
      const convRows: Array<{
        id: string;
        messages: { sender_type: string | null }[] | null;
      }> = (conversationsRes.data as typeof convRows) ?? [];
      if (convRows.length > 0) {
        // A conversation got a response when BOTH sides have spoken in it —
        // two distinct sender_types. A thread where only the investor ever
        // wrote is precisely the un-answered case this metric is meant to
        // count, and the old unread-counter proxy scored it as answered.
        const withResponse = convRows.filter(
          (c) => new Set((c.messages ?? []).map((m) => m.sender_type)).size > 1
        ).length;
        mock.matchingQuality.responseRate =
          Math.round((withResponse / convRows.length) * 1000) / 10;
      }

      // ── Matching Quality: unmatched from funnel ───────────────────────────
      const matchingRows: Array<{
        status: string;
        total: number;
        distinct_investors: number;
        distinct_startups: number;
      }> = (matchingFunnelRes.data as typeof matchingRows) ?? [];
      if (matchingRows.length > 0) {
        const newRow = matchingRows.find((r) => r.status === 'new');
        const investedRow = matchingRows.find((r) => r.status === 'invested');
        if (newRow) {
          mock.matchingQuality.unmatchedStartups = newRow.distinct_startups;
          mock.matchingQuality.unmatchedInvestors = newRow.distinct_investors;
        }
        if (investedRow) {
          // Use invested count as a proxy for connection quality numerator
          const totalDeals = matchingRows.reduce((sum, r) => sum + r.total, 0);
          mock.matchingQuality.connectionQualityScore =
            totalDeals > 0
              ? Math.min(100, Math.round((investedRow.total / totalDeals) * 100 * 5))
              : mock.matchingQuality.connectionQualityScore;
        }
      }

      // ── Churn Analysis: retention curve from v_retention_cohorts ─────────
      const cohortRows: Array<{
        cohort_week: string;
        cohort_size: number;
        w1_retained: number;
        w2_retained: number;
        w3_retained: number;
        w4_retained: number;
      }> = (retentionCohortsRes.data as typeof cohortRows) ?? [];
      if (cohortRows.length > 0) {
        // Average retention across all cohorts to build a retention curve
        const avgRetention = (week: 'w1_retained' | 'w2_retained' | 'w3_retained' | 'w4_retained') => {
          const cohortsWithData = cohortRows.filter((c) => c.cohort_size > 0);
          if (cohortsWithData.length === 0) return 0;
          const totalRetained = cohortsWithData.reduce((sum, c) => sum + c[week], 0);
          const totalCohortSize = cohortsWithData.reduce((sum, c) => sum + c.cohort_size, 0);
          return totalCohortSize > 0 ? Math.round((totalRetained / totalCohortSize) * 100) : 0;
        };
        const w1 = avgRetention('w1_retained');
        const w2 = avgRetention('w2_retained');
        const w3 = avgRetention('w3_retained');
        const w4 = avgRetention('w4_retained');
        // Map weekly retention to the day-based churn curve format
        // W1 ≈ day 7, W2 ≈ day 14, W3 ≈ day 21, W4 ≈ day 30
        // We don't have role split from cohorts so use same values for both
        mock.churnAnalysis.churnCurve = [
          { day: 1, startupRetention: 100, investorRetention: 100 },
          { day: 7, startupRetention: w1 || mock.churnAnalysis.churnCurve[2].startupRetention, investorRetention: w1 || mock.churnAnalysis.churnCurve[2].investorRetention },
          { day: 14, startupRetention: w2 || mock.churnAnalysis.churnCurve[3].startupRetention, investorRetention: w2 || mock.churnAnalysis.churnCurve[3].investorRetention },
          { day: 21, startupRetention: w3 || mock.churnAnalysis.churnCurve[3].startupRetention, investorRetention: w3 || mock.churnAnalysis.churnCurve[3].investorRetention },
          { day: 30, startupRetention: w4 || mock.churnAnalysis.churnCurve[4].startupRetention, investorRetention: w4 || mock.churnAnalysis.churnCurve[4].investorRetention },
        ];
      }

      // ── Churn Analysis: last action before churn ──────────────────────────
      const churnEventRows: Array<{ user_id: string; event_name: string; created_at: string }> =
        (churnLastActionRes.data as typeof churnEventRows) ?? [];
      if (churnEventRows.length > 0) {
        // Identify churned users: last event older than 14 days
        // Find the last event per user and tally event names
        const lastEventByUser: Map<string, string> = new Map();
        for (const row of churnEventRows) {
          if (!lastEventByUser.has(row.user_id)) {
            lastEventByUser.set(row.user_id, row.event_name);
          }
        }
        const actionCounts: Map<string, number> = new Map();
        for (const eventName of lastEventByUser.values()) {
          actionCounts.set(eventName, (actionCounts.get(eventName) ?? 0) + 1);
        }
        const total = lastEventByUser.size;
        if (total > 0) {
          const topActions = Array.from(actionCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([action, count]) => ({
              action,
              percentage: Math.round((count / total) * 100),
            }));
          // Ensure percentages add up to ≤ 100 with an "Other" bucket
          const coveredPct = topActions.reduce((sum, a) => sum + a.percentage, 0);
          if (coveredPct < 100) {
            topActions.push({ action: 'Other', percentage: 100 - coveredPct });
          }
          mock.churnAnalysis.lastActionBeforeChurn = topActions;
        }
      }

      return mock;
    },
    staleTime: 5 * 60 * 1000,
  });
}
